import React, { useEffect, useState } from 'react';
import JobDetailsPanel from './JobDetailsPanel';
import GroupDetailsPanel from './GroupDetailsPanel';
import TreeDetailsPanel from './TreeDetailsPanel';
import NewTreeForm from './NewTreeForm';

const formatLabel = (val) => {
  if (!val) return '—';
  return val.replace(/_/g, ' ');
};

const JobsPanel = ({
  jobs,
  sites = [],
  selectedJob,
  trees,
  groups,
  enums,
  species,
  lastTreeDraft,
  selectedTreeId,
  selectedTreeIds,
  selectionMode,
  openTreeDetailId,
  openGroupId,
  pendingPin,
  onSelectJob,
  onBack,
  onCreateJob,
  onFocusTree,
  onTreeShiftClick,
  onToggleTreeSelection,
  onToggleSelectionMode,
  onTogglePlaceMode,
  placeMode,
  onSelectGroup,
  onEditTree,
  onSaveJob,
  onSaveTree,
  onSaveNewTree,
  onIgnoreNewTree,
  onCloseTreeDetail,
  onCloseGroupDetail,
  onDeleteGroup,
  onRemoveTreeFromGroup,
}) => {
  const [viewTree, setViewTree] = useState(null);

  const viewGroup = openGroupId
    ? groups.find((g) => g.group_id === openGroupId)
    : null;

  useEffect(() => {
    if (!openTreeDetailId || !selectedJob || pendingPin) return;
    const tree = trees.find((t) => t.tree_id === openTreeDetailId);
    if (tree?.job_id === selectedJob.job_id) {
      setViewTree(tree);
    }
  }, [openTreeDetailId, selectedJob, trees, pendingPin]);

  useEffect(() => {
    if (viewTree) {
      const updated = trees.find((t) => t.tree_id === viewTree.tree_id);
      if (updated) setViewTree(updated);
    }
  }, [trees, viewTree?.tree_id]);

  useEffect(() => {
    if (!selectedJob) setViewTree(null);
  }, [selectedJob]);

  useEffect(() => {
    if (pendingPin) setViewTree(null);
  }, [pendingPin]);

  const handleBackToJob = () => {
    setViewTree(null);
    onCloseTreeDetail?.();
    onCloseGroupDetail?.();
  };

  const handleBackFromGroup = () => {
    onCloseGroupDetail?.();
  };

  const handleBackToList = () => {
    setViewTree(null);
    onCloseTreeDetail?.();
    onCloseGroupDetail?.();
    onBack();
  };

  const handleEditTree = (tree) => {
    setViewTree(tree);
    onCloseGroupDetail?.();
    onEditTree?.(tree);
  };

  if (pendingPin && selectedJob && !selectionMode) {
    return (
      <div>
        <button type="button" className="btn-back" onClick={onIgnoreNewTree}>
          ‹ {selectedJob.name}
        </button>
        <NewTreeForm
          key={`${pendingPin.lat}-${pendingPin.lng}`}
          job={selectedJob}
          coords={pendingPin}
          enums={enums}
          species={species}
          lastDraft={lastTreeDraft}
          onSave={onSaveNewTree}
          onIgnore={onIgnoreNewTree}
        />
      </div>
    );
  }

  if (pendingPin && !selectedJob) {
    return (
      <div className="empty-state">
        <p>Select a job first</p>
        <p style={{ fontSize: 12, marginTop: 8 }}>Open a job, then long-press the map to add a tree.</p>
        <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 12, color: '#333', background: '#eee' }} onClick={onIgnoreNewTree}>
          Dismiss pin
        </button>
      </div>
    );
  }

  if (viewTree && selectedJob) {
    return (
      <div>
        <button type="button" className="btn-back" onClick={handleBackToJob}>
          ‹ {selectedJob.name}
        </button>
        <TreeDetailsPanel
          tree={viewTree}
          enums={enums}
          onSave={(data, cb) => onSaveTree?.(data, cb)}
        />
      </div>
    );
  }

  if (viewGroup && selectedJob) {
    return (
      <div>
        <button type="button" className="btn-back" onClick={handleBackFromGroup}>
          ‹ {selectedJob.name}
        </button>
        <GroupDetailsPanel
          group={viewGroup}
          trees={trees}
          selectedTreeId={selectedTreeId}
          selectedTreeIds={selectedTreeIds}
          onSelectTree={(tree, opts) => {
            if (opts?.shift || selectionMode) onToggleTreeSelection?.(tree);
            else onFocusTree?.(tree);
          }}
          onEditTree={handleEditTree}
          onDeleteGroup={onDeleteGroup}
          onRemoveTree={onRemoveTreeFromGroup}
        />
      </div>
    );
  }

  if (selectedJob) {
    return (
      <div>
        <button type="button" className="btn-back" onClick={handleBackToList}>
          ‹ Jobs
        </button>
        <JobDetailsPanel
          job={selectedJob}
          trees={trees}
          groups={groups}
          enums={enums}
          selectedTreeId={selectedTreeId}
          selectedTreeIds={selectedTreeIds}
          selectionMode={selectionMode}
          onSelectTree={onFocusTree}
          onToggleTreeSelection={onToggleTreeSelection}
          onToggleSelectionMode={onToggleSelectionMode}
          onTogglePlaceMode={onTogglePlaceMode}
          placeMode={placeMode}
          onSelectGroup={onSelectGroup}
          onEditTree={handleEditTree}
          onSaveJob={onSaveJob}
        />
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        className="btn btn-primary btn-touch"
        style={{ width: '100%', marginBottom: 16 }}
        onClick={onCreateJob}
        disabled={sites.length === 0}
        title={sites.length === 0 ? 'Create a site first' : undefined}
      >
        + Create job
      </button>
      {sites.length === 0 && (
        <p className="empty-inline" style={{ marginBottom: 16, fontSize: 12 }}>
          Switch to Sites and create a site before adding jobs.
        </p>
      )}
      {jobs.length === 0 && (
        <div className="empty-state">
          <p>No jobs yet</p>
        </div>
      )}
      {jobs.map((job) => {
        const treeCount = trees.filter((t) => t.job_id === job.job_id).length;
        return (
          <div
            key={job.job_id}
            className="job-list-item job-list-item-touch"
            onClick={() => onSelectJob(job)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onSelectJob(job)}
          >
            <div className="list-item-headline">
              <h4>{job.name}</h4>
              <span className="list-item-meta">{formatLabel(job.status)} · {treeCount} trees</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default JobsPanel;
